import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { captureMessage } from '@sentry/react';
import { getBlogPosts } from '@/api';
import useApiCall from '@/hooks/useApiCall';
import useElementEvent from '@/hooks/useElementEvent';
import { appendToList } from '@/utils/tools';

interface PostsLoaderProps {
  lang: string;
  tag?: string | null;
}

interface BlogResult {
  items: string[];
  has_next: boolean;
}

const PostsLoader = ({ lang, tag }: PostsLoaderProps) => {
  const [page, setPage] = useState(2);

  const buttonRef = useRef<HTMLElement | null>(null);

  const onSuccess = useCallback((response: { data?: { result?: BlogResult } }) => {
    const result = response.data?.result;

    if (!result) {
      captureMessage('PostsLoader: empty result from API', { level: 'warning' });
      return;
    }

    appendToList('#posts-list', result.items);

    if (!result.has_next && buttonRef.current) {
      (buttonRef.current as HTMLElement).style.display = 'none';
    }

    setPage((prevPage) => prevPage + 1);
  }, []);

  const onSubmit = useCallback(() => {
    const options = {
      lang,
      page,
      tag: tag ?? undefined
    };
    return getBlogPosts(options);
  }, [lang, page, tag]);

  const {
    callback,
    processing
  } = useApiCall(onSubmit, onSuccess);

  useEffect(() => {
    buttonRef.current = document.querySelector('#blog-load-more');
  }, []);

  useEffect(() => {
    if (buttonRef.current) {
      (buttonRef.current as HTMLButtonElement).disabled = processing;
    }
  }, [processing]);

  useElementEvent('#blog-load-more', callback);

  return null;
};

export default PostsLoader;
